// A CCN WITH A TYPO IS STILL SIX DIGITS.
//
// The CCN is how a facility reaches its public CMS record — its star ratings,
// its citation history, its readiness brief. Nothing else in the product joins
// to that data. So the CCN field is the single point where one mistyped digit
// silently swaps in ANOTHER NURSING HOME'S survey history, and every downstream
// screen reports it with full confidence.
//
// It is not a remote risk. CCNs are densely allocated — roughly 15,000 skilled
// nursing facilities across a six-digit space whose first two digits are the
// state code — so a transposed digit very often lands on a real, different
// building in the same state. There is no format check that can catch it,
// because the wrong answer is perfectly well-formed.
//
// ── what this file does, and what it deliberately does NOT do ───────────────
//
// It does not decide whether a CCN is correct. A person does that, by reading
// the provider name and town that CMS returns for the number they typed.
//
// All this decides is HOW LOUD TO BE about what came back: a quiet confirmation
// when the names plainly correspond, a warning when they do not. The provider
// name is shown either way — a match verdict must never replace the evidence,
// because two facilities in one chain can have near-identical names.
//
// It never blocks a save. Legitimate rebrands, acquisitions and chain renames
// mean the CMS name genuinely disagrees with the operator's name, and a hard
// block would make correct data impossible to enter.

// Words that appear in a large share of skilled-nursing names and therefore
// carry no evidence that two names refer to the same building.
//
// THIS LIST IS THE WHOLE DIFFICULTY. "Golden Living Center" and "Sunrise Care
// Center" share a token, and a matcher counting raw overlap would call that a
// match and suppress the warning on a genuinely wrong CCN. Overlap only counts
// on what is left after these come out.
const GENERIC = new Set([
  // grammar
  "THE", "AND", "OF", "AT", "FOR", "A", "AN", "ON", "IN",
  // what nearly every one of them is
  "NURSING", "NURSE", "REHABILITATION", "REHAB", "REHABILITATIVE",
  "HEALTH", "HEALTHCARE", "CARE", "CENTER", "CENTRE", "CTR",
  "HOME", "HOUSE", "FACILITY", "LIVING", "SKILLED", "SNF",
  "CONVALESCENT", "EXTENDED", "SUBACUTE", "POST", "ACUTE",
  "TRANSITIONAL", "SENIOR", "SENIORS", "RETIREMENT",
  "COMMUNITY", "COMMUNITIES", "RESIDENCE", "RESIDENTIAL",
  // corporate form
  "LLC", "INC", "LP", "LLP", "LTD", "CORP", "CORPORATION", "CO",
  "COMPANY", "GROUP", "LIMITED", "PARTNERSHIP", "OPERATIONS", "OPCO",
]);

// Spelling variants that are the same word. Applied before the generic filter
// so "Saint Josephs" and "ST JOSEPH" reduce to the same distinctive token.
const SYNONYM = { SAINT: "ST", MOUNT: "MT", MOUNTAIN: "MTN", FORT: "FT", "&": "AND" };

/** Split a facility name into comparable tokens, as they are actually spelled. */
export function tokens(name) {
  const s = String(name ?? "")
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
  if (!s) return [];
  return s.split(" ").map((t) => SYNONYM[t] || t);
}

/**
 * The form a token is COMPARED in — never the form it is shown in.
 *
 * "ST JOSEPHS" and "ST JOSEPH" are one name, so a trailing S comes off. That
 * rule also mangles "JAMES" into "JAME", which is harmless for comparison
 * (both sides are stemmed identically) and would be embarrassing on screen:
 * the matcher tells the user WHICH words it matched on, and "both names contain
 * JAME" reads like a bug.
 *
 * Hence the split. Stems decide, original spellings are displayed.
 */
export function stem(token) {
  const t = String(token ?? "");
  return t.length > 3 && t.endsWith("S") && !t.endsWith("SS") ? t.slice(0, -1) : t;
}

/** The tokens that actually carry identity, spelled as the name spells them. */
export function distinctive(name) {
  return [...new Set(tokens(name).filter((t) => !GENERIC.has(t)))];
}

/**
 * How loud to be about a CCN's provider name.
 *
 * Returns one of three verdicts, and "unclear" is a real answer rather than a
 * hedge: if a name reduces to nothing but generic words there is no evidence
 * either way, and claiming a match from that would be inventing confidence.
 *
 *   match     the names share something distinctive — show a quiet confirmation
 *   mismatch  they share nothing — warn, because this is what a typo looks like
 *   unclear   one of the names carries no distinctive word at all
 */
export function ccnNameSignal(facilityName, providerName) {
  const ours = distinctive(facilityName);
  const theirs = distinctive(providerName);

  if (!ours.length || !theirs.length) {
    return {
      verdict: "unclear",
      shared: [],
      why: "There is no distinctive word in one of these names to compare on — check the town and the name yourself.",
    };
  }

  // Compared on stems, reported in our own spelling.
  const theirStems = new Set(theirs.map(stem));
  const shared = ours.filter((t) => theirStems.has(stem(t)));

  if (shared.length) {
    return {
      verdict: "match",
      shared,
      why: `Both names contain ${shared.join(", ")}.`,
    };
  }

  return {
    verdict: "mismatch",
    shared: [],
    // Says what it IS rather than what it proves. A rebrand looks exactly like
    // a typo from here, and only the person entering it knows which.
    why:
      "These two names have no significant word in common. That is what a mistyped digit looks like — " +
      "and also what a recent rebrand looks like. Check the town below before saving.",
  };
}

const ccnMatch = { tokens, stem, distinctive, ccnNameSignal };
export default ccnMatch;
